import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbstractFieldComponent } from "abreact/input/markdown/nodes/AbstractFieldComponent";
import { FIELD_TYPE, getAbstractFieldExtension, getInputFieldExtension, getParamFieldExtension } from "abreact/input/markdown/utils/MarkdownUtils";
import { AbChiclet } from "absdk/commons/ui/misc/AbChiclet";
import { EnumUtils } from "abweb/utils/EnumUtils";
import { StringUtils } from "abweb/utils/StringUtils";
import { $applyNodeReplacement, DecoratorNode } from 'lexical';
import React from "react";
function convertFieldElement(domNode) {
  const techName = domNode.getAttribute('data-lexical-tech-name');
  const displayName = domNode.getAttribute('data-lexical-display-name');
  const fieldTypeString = domNode.getAttribute('data-lexical-field-type');
  const businessName = domNode.getAttribute('data-lexical-field-biz-name');
  const canonicalName = domNode.getAttribute('data-lexical-field-canonical-name');
  const typeIcon = domNode.getAttribute('data-lexical-type-icon');
  const fieldType = EnumUtils.enumValueByValue(FIELD_TYPE, fieldTypeString ?? "");
  let obj;
  if (techName != null) {
    if (canonicalName != null) {
      //is input
      obj = {
        techName: techName,
        businessName: businessName || displayName || techName,
        canonicalName: canonicalName || techName,
        typeIcon: typeIcon ?? ""
      };
    } else {
      obj = {
        techName: techName,
        typeIcon: typeIcon ?? "string"
      };
    }
    const node = $createAbstractFieldNode({
      displayName: displayName || techName,
      fieldDef: obj,
      type: fieldType
    });
    return {
      node
    };
  }
  return null;
}
export class AbstractFieldNode extends DecoratorNode {
  static getType() {
    return 'field';
  }
  static clone(node) {
    return new AbstractFieldNode(node.__displayName, node.__object, node.__fieldType, node.__viewMode, node.__key);
  }
  constructor(displayName, info, type, viewMode, key) {
    super(key);
    _defineProperty(this, "__displayName", void 0);
    _defineProperty(this, "__fieldType", void 0);
    _defineProperty(this, "__object", void 0);
    _defineProperty(this, "__testName", void 0);
    _defineProperty(this, "__viewMode", void 0);
    this.__object = info;
    this.__displayName = StringUtils.isNotNullOrEmpty(displayName) ? displayName : this.__object.techName;
    this.__fieldType = type ?? FIELD_TYPE.NONE;
    this.__testName = undefined;
    this.__viewMode = viewMode !== undefined ? viewMode : "default";
  }
  static importJSON(serializedNode) {
    const obj = serializedNode.fieldDef;
    let outputObj;
    if (serializedNode.fieldType === FIELD_TYPE.PARAM) {
      outputObj = obj;
    } else if (serializedNode.fieldType === FIELD_TYPE.INPUT) {
      outputObj = obj;
    } else {
      outputObj = obj;
    }
    return $createAbstractFieldNode({
      displayName: serializedNode.displayName,
      type: serializedNode.fieldType,
      fieldDef: outputObj
    });
  }
  exportJSON() {
    return {
      displayName: this.__displayName,
      type: 'field',
      version: 1,
      fieldType: this.__fieldType,
      fieldDef: this.__object
    };
  }
  updateDOM(prevNode) {
    return prevNode.__object.techName !== prevNode.__object.techName || prevNode.__displayName !== prevNode.__displayName;
  }
  createDOM() {
    const dom = document.createElement('span');
    dom.style.display = 'inline-block';
    return dom;
  }
  exportDOM() {
    const element = document.createElement('span');
    element.setAttribute('data-lexical-field', 'true');
    element.setAttribute('data-lexical-tech-name', this.__object.techName);
    element.setAttribute('data-lexical-display-name', this.__displayName);
    element.setAttribute('data-lexical-field-type', this.__fieldType.toString());
    element.setAttribute('data-lexical-type-icon', this.__object.typeIcon);
    if ("canonicalName" in this.__object) {
      element.setAttribute('data-lexical-field-canonical-name', this.__object.canonicalName);
    }
    if ("businessName" in this.__object) {
      element.setAttribute('data-lexical-field-biz-name', this.__object.businessName);
    }
    return {
      element
    };
  }
  static importDOM() {
    return {
      span: domNode => {
        if (!domNode.hasAttribute('data-lexical-field')) {
          return null;
        }
        return {
          conversion: convertFieldElement,
          priority: 1
        };
      }
    };
  }
  decorate() {
    let node;
    let extension;
    switch (this.__fieldType) {
      case FIELD_TYPE.INPUT:
        extension = getInputFieldExtension();
        break;
      case FIELD_TYPE.PARAM:
        extension = getParamFieldExtension();
        break;
      default:
        extension = getAbstractFieldExtension();
    }
    if (extension) {
      node = extension.render({
        displayName: this.__displayName,
        fieldDef: this.__object,
        testName: this.__testName,
        viewMode: this.__viewMode
      }, this.__displayName);
      if (! /*#__PURE__*/React.isValidElement(node)) {
        throw new Error("Field extension did not return element" + node);
      }
    } else {
      // no extension, still render something.
      const classname = this.__fieldType === FIELD_TYPE.INPUT ? "ab-markdown-sample-abstract-field ab-markdown-field-input" : "ab-markdown-sample-abstract-field ab-markdown-field-param";
      const icon = this.__fieldType === FIELD_TYPE.INPUT ? "absdk:source-input" : "absdk:bre-var-type-parameter";
      node = /*#__PURE__*/React.createElement("span", {
        style: {
          display: "inline-block"
        },
        className: classname
      }, /*#__PURE__*/React.createElement(AbChiclet, {
        icon: icon,
        label: this.__displayName,
        tooltip: this.__object.techName
      }));
    }
    return /*#__PURE__*/React.createElement(AbstractFieldComponent, {
      nodeKey: this.__key
    }, node);
  }
  getTechName() {
    return this.__object.techName;
  }
  setTechName(value) {
    const writable = this.getWritable();
    writable.__object.techName = value;
  }
  getDisplayName() {
    return this.__displayName;
  }
  setDisplayName(displayName) {
    const writable = this.getWritable();
    writable.__displayName = displayName;
  }
  getTestName() {
    return this.__testName;
  }
  setTestName(displayName) {
    const writable = this.getWritable();
    writable.__testName = displayName;
  }
  setTestView(viewMode) {
    const writable = this.getWritable();
    writable.__viewMode = viewMode;
  }
  getTestView() {
    return this.__viewMode;
  }
  getIsInput() {
    return this.__fieldType === FIELD_TYPE.INPUT;
  }
  getFieldType() {
    return this.__fieldType;
  }
  getIsParam() {
    return this.__fieldType === FIELD_TYPE.PARAM;
  }
  getIsGeneral() {
    return this.__fieldType === FIELD_TYPE.NONE;
  }
  getCanonicalDisplayName() {
    return "canonicalName" in this.__object ? this.__object.canonicalName.replaceAll("_SPACE_", " ") : "";
  }
  getCanonicalName() {
    return "canonicalName" in this.__object ? this.__object.canonicalName : "";
  }
  getBusinessName() {
    return "businessName" in this.__object ? this.__object.businessName : "";
  }
  getTypeIcon() {
    return this.__object.typeIcon;
  }
  getBreEncoding() {
    return "breEncoding" in this.__object ? this.__object.breEncoding : "";
  }
}
// Is there a Field extension registered? null means no. Starts undefined.
_defineProperty(AbstractFieldNode, "extension", undefined);
export function $createAbstractFieldNode(obj) {
  const fieldNode = new AbstractFieldNode(obj.displayName, obj.fieldDef, obj.type, obj.viewMode);
  return $applyNodeReplacement(fieldNode);
}
export function $isAbstractFieldNode(node) {
  // eslint-disable-next-line abinitio/no-instanceof
  return node instanceof AbstractFieldNode;
}
//# sourceMappingURL=AbstractFieldNode.js.map
